import type { Column, Header, Table } from '@tanstack/react-table';
import type { CSSProperties } from 'react';
import * as R from 'remeda';
import { logger } from '../../../lib/logger';
import type { PinnedColumnInfo } from '../helpers/types';
import { getWidth } from '../helpers/utils';

// ─── 純粋関数ヘルパー ───

/** 単一カラムの固定幅 / 最小幅スタイルを導出する */
const resolveColumnStyle = <TData>(column: Column<TData, unknown>): CSSProperties | undefined => {
  const width = getWidth(column.columnDef.meta?.width);
  const minWidth = getWidth(column.columnDef.meta?.minWidth);

  if (width && width !== 'auto') {
    return {
      width: `${width}px`,
      minWidth: `${minWidth ?? width}px`,
      maxWidth: `${width}px`,
    };
  }
  if (minWidth && minWidth !== 'auto') {
    return { minWidth: `${minWidth}px` };
  }
  return undefined;
};

/** リーフカラム → { [columnId]: style } のマッピングを構築する */
const buildColumnStyles = <TData>(
  columns: Column<TData, unknown>[],
): Record<string, CSSProperties | undefined> =>
  R.mapToObj(columns, (col) => [col.id, resolveColumnStyle(col)]);

/** 数値幅をもつカラムの固定幅を合算する */
const sumFixedWidths = <TData>(columns: Column<TData, unknown>[]): number =>
  R.sumBy(columns, (col) => {
    const width = getWidth(col.columnDef.meta?.width);
    return typeof width === 'number' ? width : 0;
  });

/**
 * グループ幅ウェイトからリーフカラムの割合幅を計算する
 *
 * groupWidthWeight が一つも指定されていない場合は空オブジェクトを返す（ウェイト計算不要）
 */
const buildWeightedLeafWidthStyles = <TData>(
  topHeaders: Header<TData, unknown>[],
): Record<string, CSSProperties> => {
  const hasGroupWeight = topHeaders.some(
    (h) => h.column.columnDef.meta?.groupWidthWeight !== undefined,
  );
  if (!hasGroupWeight) return {};

  const totalGroupWeight = R.sumBy(
    topHeaders,
    (h) => h.column.columnDef.meta?.groupWidthWeight ?? 1,
  );
  if (totalGroupWeight <= 0) return {};

  return R.pipe(
    topHeaders,
    R.flatMap((header) => {
      const groupWeight = header.column.columnDef.meta?.groupWidthWeight ?? 1;
      const leafHeaders = header.getLeafHeaders();
      if (leafHeaders.length === 0) return [];

      const leafWeightSum = R.sumBy(
        leafHeaders,
        (leaf) => leaf.column.columnDef.meta?.columnWidthWeight ?? 1,
      );

      return R.map(leafHeaders, (leaf) => {
        const leafWeight = leaf.column.columnDef.meta?.columnWidthWeight ?? 1;
        const widthPercent =
          (groupWeight / totalGroupWeight) * (leafWeightSum > 0 ? leafWeight / leafWeightSum : 0);
        return [leaf.column.id, { width: `${widthPercent * 100}%` }] as [string, CSSProperties];
      });
    }),
    R.fromEntries(),
  );
};

type PinnedAccumulator = {
  offset: number;
  entries: [string, PinnedColumnInfo][];
};

/** 片側（左 or 右）のピン留めエントリを構築する */
const buildSidePinnedEntries = <TData>({
  columns,
  side,
}: {
  columns: Column<TData, unknown>[];
  side: 'left' | 'right';
}): [string, PinnedColumnInfo][] => {
  const ordered = side === 'right' ? [...columns].reverse() : columns;

  const { entries } = ordered.reduce<PinnedAccumulator>(
    (acc, column, index) => {
      const width = getWidth(column.columnDef.meta?.width);

      if (typeof width !== 'number') {
        logger.debug(
          `[Table] pinned カラム "${column.id}" には明示的な数値幅（meta.width）が必要です。現在の値: ${column.columnDef.meta?.width}`,
        );
        return acc;
      }

      const isEdge = index === ordered.length - 1;
      return {
        offset: acc.offset + width,
        entries: [
          ...acc.entries,
          [
            column.id,
            {
              style: {
                position: 'relative' as const,
                zIndex: 10,
              },
              side,
              pinnedOffset: acc.offset,
              isLastLeftPinned: side === 'left' && isEdge,
              isFirstRightPinned: side === 'right' && isEdge,
            },
          ],
        ],
      };
    },
    { offset: 0, entries: [] },
  );

  return entries;
};

/**
 * ピン留めカラムのスタイル（position: relative + zIndex + transformベースのオフセット）を計算する
 *
 * ピン留めカラムが存在しない場合は空オブジェクトを返す（計算スキップ）
 */
const buildPinnedColumnStyles = <TData>(
  columns: Column<TData, unknown>[],
): Record<string, PinnedColumnInfo> => {
  const leftPinned = columns.filter((c) => c.columnDef.meta?.pinned === 'left');
  const rightPinned = columns.filter((c) => c.columnDef.meta?.pinned === 'right');

  const hasPinnedColumns = leftPinned.length > 0 || rightPinned.length > 0;
  if (!hasPinnedColumns) return {};

  return Object.fromEntries([
    ...buildSidePinnedEntries({ columns: leftPinned, side: 'left' }),
    ...buildSidePinnedEntries({ columns: rightPinned, side: 'right' }),
  ]);
};

// ─── フック本体 ───

/**
 * テーブルのカラムスタイルを計算するフック
 *
 * @param table - TanStack Tableのインスタンス
 * @returns columnStyles, weightedLeafWidthStyles, tableFixedWidth, pinnedColumnStyles
 */
export const useTableColumnStyles = <TData>(table: Table<TData>) => {
  const columnStyles = buildColumnStyles(table.getAllLeafColumns());

  const weightedLeafWidthStyles = buildWeightedLeafWidthStyles(
    table.getHeaderGroups()[0]?.headers ?? [],
  );

  const tableFixedWidth = sumFixedWidths(table.getAllLeafColumns());

  const pinnedColumnStyles = buildPinnedColumnStyles(table.getAllLeafColumns());

  return {
    columnStyles,
    weightedLeafWidthStyles,
    tableFixedWidth,
    pinnedColumnStyles,
  };
};
